import React, {useState, useEffect} from "react";
import axios from "axios";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import {bindActionCreators} from "redux";
import {connect} from "react-redux";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import * as snackbarActions from "../../actions/snackbarActions";
import MainSnackbar from "../Utils/Snackbar/SmsSnackbar";
import CustomDialog from "../Utils/Dialogs/CustomDialog";
import TextField from "../Utils/FormInputs/TextField";
import Label from "../Utils/FormInputs/Label";
import {StickyHeadTable, CustomTableCell, MuiPaper} from "../Utils/Tables/StickyHeaderTable";
import {config} from "../../Helpers/env";
import {errorMessages} from "../../Helpers/ErrorMessages";

const headData = [
    {id: 'sl', label: '#', minWidth: 50},
    {id: 'name', label: 'Name', minWidth: 150},
    {id: 'location', label: 'Location', minWidth: 150},
    {id: 'capacity', label: 'Capacity', minWidth: 100},
    {id: 'status', label: 'Status', minWidth: 100},
    {id: 'actions', label: 'Actions', minWidth: 150},
];

const emptyForm = {name: '', location: '', capacity: ''};

const TheatreList = ({actions, snackbars}) => {
    const [theatres, setTheatres] = useState([]);
    const [loading, setLoading] = useState('idle');
    const [openDialog, setOpenDialog] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitted, setSubmitted] = useState(false);
    const [isResolved, setIsResolved] = useState(0);

    useEffect(() => {
        setLoading('loading');
        axios.post(`${config.smsUrl}/theatre/list`).then(res => {
            const data = res.data ?? {};
            const list = data.data ?? [];
            setTheatres(list);
            setLoading('success');
        }).catch(err => {
            errorMessages(err, null, actions);
            setLoading('error');
        });
    }, [isResolved]);

    const handleOpenAdd = () => {
        setEditId(null);
        setForm(emptyForm);
        setSubmitted(false);
        setOpenDialog(true);
    };

    const handleOpenEdit = (theatre) => {
        setEditId(theatre.theatre_id);
        setForm({
            name: theatre.name ?? '',
            location: theatre.location ?? '',
            capacity: theatre.capacity ?? '',
        });
        setSubmitted(false);
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditId(null);
        setForm(emptyForm);
        setSubmitted(false);
    };

    const handleChange = (e) => {
        const {name, value} = e.target;
        setForm(prev => ({...prev, [name]: value}));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        if (!form.name || !form.location || !form.capacity) return;

        if (editId) {
            axios.post(`${config.smsUrl}/theatre/update`, {
                theatre_id: editId,
                name: form.name,
                location: form.location,
                capacity: Number(form.capacity),
                is_active: true,
            }).then(() => {
                actions.snackbarActions.snackSuccess('Theatre updated successfully');
                setIsResolved(prev => prev + 1);
                handleClose();
            }).catch(err => {
                errorMessages(err, null, actions);
            });
        } else {
            axios.post(`${config.smsUrl}/theatre/create`, {
                name: form.name,
                location: form.location,
                capacity: Number(form.capacity),
            }).then(() => {
                actions.snackbarActions.snackSuccess('Theatre created successfully');
                setIsResolved(prev => prev + 1);
                handleClose();
            }).catch(err => {
                errorMessages(err, null, actions);
            });
        }
    };

    const handleDeactivate = (theatre) => {
        axios.post(`${config.smsUrl}/theatre/update`, {
            theatre_id: theatre.theatre_id,
            name: theatre.name,
            location: theatre.location,
            capacity: theatre.capacity,
            is_active: false,
        }).then(() => {
            actions.snackbarActions.snackSuccess('Theatre deactivated successfully');
            setIsResolved(prev => prev + 1);
        }).catch(err => {
            errorMessages(err, null, actions);
        });
    };

    const closeSnackbar = () => {
        actions.snackbarActions.hideSnackbar();
    };

    const {openBar, type, message} = snackbars;
    const isLoading = loading === 'loading';
    const isSuccess = loading === 'success';
    const isError = loading === 'error';

    return (
        <div>
            <MainSnackbar variant={type} open={openBar} message={message} handleCloseBar={closeSnackbar}/>

            <CustomDialog
                open={openDialog}
                handleClose={handleClose}
                title={editId ? 'Edit Theatre' : 'Add Theatre'}
                maxWidth="md"
            >
                <form onSubmit={handleSubmit} autoComplete="off">
                    <div className="row">
                        <Form.Group as={Col} md={6}>
                            <Label name="Name" htmlFor="theatre_name" type/>
                            <TextField
                                type="text"
                                name="name"
                                id="theatre_name"
                                value={form.name}
                                onChange={handleChange}
                                submitted={submitted}
                            />
                            {submitted && !form.name &&
                                <div className="help-block">Theatre name is required</div>}
                        </Form.Group>
                        <Form.Group as={Col} md={6}>
                            <Label name="Location" htmlFor="theatre_location" type/>
                            <TextField
                                type="text"
                                name="location"
                                id="theatre_location"
                                value={form.location}
                                onChange={handleChange}
                                submitted={submitted}
                            />
                            {submitted && !form.location &&
                                <div className="help-block">Location is required</div>}
                        </Form.Group>
                        <Form.Group as={Col} md={6}>
                            <Label name="Capacity" htmlFor="theatre_capacity" type/>
                            <TextField
                                type="number"
                                name="capacity"
                                id="theatre_capacity"
                                value={form.capacity}
                                onChange={handleChange}
                                submitted={submitted}
                                min={1}
                            />
                            {submitted && !form.capacity &&
                                <div className="help-block">Capacity is required</div>}
                        </Form.Group>
                    </div>
                    <div className="text-right mt-3">
                        <button type="button" className="btn btn-sm sms-grey-btn mr-2" onClick={handleClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-sm sms-btn">
                            {editId ? 'Update Theatre' : 'Save Theatre'}
                        </button>
                    </div>
                </form>
            </CustomDialog>

            <div className="text-right mb-3" style={{paddingTop: '1rem'}}>
                <button className="btn btn-sm sms-info-btn" onClick={handleOpenAdd}>
                    Add Theatre
                </button>
            </div>

            <MuiPaper>
                <StickyHeadTable
                    width="100%"
                    columns={headData}
                >
                    {isLoading ? (
                        <TableRow>
                            <TableCell align="center" colSpan={headData.length}>
                                <img src="/images/smsloader.gif" alt="loader"/>
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {isSuccess ? (
                        theatres.length > 0 ? (
                            theatres.map((item, index) => (
                                <TableRow tabIndex={-1} key={item.theatre_id ?? index}>
                                    <CustomTableCell>{index + 1}</CustomTableCell>
                                    <CustomTableCell>{item.name}</CustomTableCell>
                                    <CustomTableCell>{item.location}</CustomTableCell>
                                    <CustomTableCell>{item.capacity}</CustomTableCell>
                                    <CustomTableCell>
                                        {item.is_active !== false
                                            ? <span className="badge badge-success">Active</span>
                                            : <span className="badge badge-danger">Inactive</span>
                                        }
                                    </CustomTableCell>
                                    <CustomTableCell>
                                        <button
                                            className="btn btn-sm sms-info-btn mr-1"
                                            onClick={() => handleOpenEdit(item)}
                                        >
                                            Edit
                                        </button>
                                        {item.is_active !== false && (
                                            <button
                                                className="btn btn-sm sms-btn-dismiss"
                                                onClick={() => handleDeactivate(item)}
                                            >
                                                Deactivate
                                            </button>
                                        )}
                                    </CustomTableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell align="center" colSpan={headData.length}>
                                    No theatres found
                                </TableCell>
                            </TableRow>
                        )
                    ) : null}
                    {isError ? (
                        <TableRow>
                            <TableCell align="center" colSpan={headData.length}>
                                The server did not return a valid response
                            </TableCell>
                        </TableRow>
                    ) : null}
                </StickyHeadTable>
            </MuiPaper>
        </div>
    );
};

function mapStateToProps(state) {
    return {snackbars: state.snackbar};
}

function mapDispatchToProps(dispatch) {
    return {actions: {snackbarActions: bindActionCreators(snackbarActions, dispatch)}};
}

export default connect(mapStateToProps, mapDispatchToProps)(TheatreList);
